class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.handleCartOpen = this.handleCartOpen.bind(this);
    this.handleCartClose = this.handleCartClose.bind(this);
  }

  connectedCallback() {
    document.addEventListener('missence:cart:open', this.handleCartOpen);
    document.addEventListener('missence:cart:close', this.handleCartClose);
  }

  disconnectedCallback() {
    document.removeEventListener('missence:cart:open', this.handleCartOpen);
    document.removeEventListener('missence:cart:close', this.handleCartClose);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
    document.dispatchEvent(
      new CustomEvent('missence:cart:drawer-opened', {
        detail: {
          source: 'cart-drawer',
        },
      })
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
    document.dispatchEvent(
      new CustomEvent('missence:cart:drawer-closed', {
        detail: {
          source: 'cart-drawer',
        },
      })
    );
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerHtml = parsedState?.sections?.['cart-drawer'];
    const drawerSection = drawerHtml ? new DOMParser().parseFromString(drawerHtml, 'text/html') : null;
    const renderedDrawer = drawerSection?.querySelector('cart-drawer');
    const renderedItems = drawerSection?.querySelector('cart-drawer-items');
    const isEmpty =
      typeof parsedState?.item_count === 'number'
        ? parsedState.item_count === 0
        : Boolean(renderedDrawer?.classList.contains('is-empty') || renderedItems?.classList.contains('is-empty'));

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.classList.toggle('is-empty', isEmpty);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);

    if (window.__missenceSuppressNextCartDrawer) {
      window.__missenceSuppressNextCartDrawer = false;
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      document.dispatchEvent(
        new CustomEvent('missence:cart:drawer-suppressed', {
          detail: {
            source: 'cart-drawer',
            cartData: parsedState,
            productVariantId: parsedState.id,
          },
        })
      );
      return;
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });

    document.dispatchEvent(
      new CustomEvent('missence:cart:updated', {
        detail: {
          source: 'cart-drawer',
          cartData: parsedState,
          productVariantId: parsedState.id,
        },
      })
    );
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  handleCartOpen(event) {
    this.open(event.detail && event.detail.triggeredBy ? event.detail.triggeredBy : undefined);
  }

  handleCartClose() {
    this.close();
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#shopify-section-cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
